/**
 * Paywall page - explicit access boundary for sponsor-gated routes.
 * 
 * ACT 5: Deferred-payment access gate.
 * This is the canonical access boundary before /discover, /card/:id, and /intro-request.
 * 
 * Access state is explicit and separate from real payment:
 * - Payment integration is deferred (Epic 4)
 * - Access is currently granted manually/admin/dev-side
 * - No real payment behavior is implemented here
 * 
 * IMPORTANT: This page intentionally has no card/checkout/billing UI.
 * Real payment integration will be added when Epic 4 resumes.
 */
import { Link } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { useAuthStore, SponsorAccessState } from '@/store/authStore';

/**
 * Maps access state to display content.
 * Each state has explicit messaging about the access gate status.
 */
function getAccessContent(state: SponsorAccessState) {
  switch (state) {
    case 'unknown':
      return {
        title: 'Checking Access',
        description: 'Verifying your sponsor access status...',
        type: 'processing' as const,
      };
    case 'no_access':
      return {
        title: 'Access Restricted',
        description: 'You need sponsor access to view curated discovery and intro requests.',
        type: 'restricted' as const,
      };
    case 'manual_access':
      return {
        title: 'Sponsor Access Granted',
        description: 'You have manual sponsor access. Enjoy full discovery features.',
        type: 'granted' as const,
      };
    case 'paid_placeholder':
      return {
        title: 'Sponsor Access Granted',
        description: 'Your sponsor access is active. Enjoy full discovery features.',
        type: 'granted' as const,
      };
  }
}

export function PaywallPage() {
  const { sponsorAccessState } = useAuthStore();
  const access = getAccessContent(sponsorAccessState);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Access InDeep</h1>
        <p className="page-description">
          Unlock curated discovery and intro requests.
        </p>
      </div>

      <div className="paywall-content">
        {/* Main access status */}
        <div className={`access-status access-${access.type}`}>
          <h2>{access.title}</h2>
          <p>{access.description}</p>
        </div>

        {/* Deferred payment notice */}
        <div className="deferred-notice">
          <h3>Payment Integration Status</h3>
          <p>
            <strong>Deferred.</strong> Real payment processing will be implemented 
            in a future update. Currently, access is granted manually or through 
            development/test channels.
          </p>
          <p className="notice-disclaimer">
            This is not a checkout page. No payment card, billing address, or 
            financial information is collected here.
          </p>
        </div>

        {/* Access granted CTA */}
        {(sponsorAccessState === 'manual_access' || sponsorAccessState === 'paid_placeholder') && (
          <div className="access-cta">
            <Link to={RoutePaths.DISCOVER} className="btn-primary">
              Browse Discovery
            </Link>
          </div>
        )}
      </div>

      <style>{`
        .paywall-content {
          margin-top: var(--spacing);
          display: flex;
          flex-direction: column;
          gap: var(--spacing);
        }
        .access-status {
          padding: var(--spacing);
          border-radius: var(--radius);
          border: 1px solid var(--color-border);
          background: var(--color-surface);
        }
        .access-status h2 {
          font-size: 1.2rem;
          margin-bottom: 8px;
        }
        .access-status p {
          color: var(--color-text-muted);
          margin-bottom: 0;
        }
        .access-processing {
          border-color: var(--color-border);
        }
        .access-restricted {
          border-color: var(--color-accent);
          background: rgba(233, 69, 96, 0.05);
        }
        .access-restricted h2 {
          color: var(--color-accent);
        }
        .access-granted {
          border-color: #2ecc71;
          background: rgba(46, 204, 113, 0.05);
        }
        .access-granted h2 {
          color: #2ecc71;
        }
        .deferred-notice {
          padding: var(--spacing);
          border-radius: var(--radius);
          border: 1px dashed var(--color-border);
          background: transparent;
        }
        .deferred-notice h3 {
          font-size: 1rem;
          margin-bottom: 8px;
          color: var(--color-text);
        }
        .deferred-notice p {
          color: var(--color-text-muted);
          margin-bottom: 8px;
          font-size: 0.9rem;
        }
        .deferred-notice p:last-child {
          margin-bottom: 0;
        }
        .notice-disclaimer {
          font-style: italic;
          font-size: 0.85rem;
          color: var(--color-text-muted);
        }
        .access-cta {
          text-align: center;
          margin-top: var(--spacing);
        }
        .btn-primary {
          display: inline-block;
          background: var(--color-accent);
          color: white;
          padding: 12px 24px;
          border-radius: var(--radius);
          text-decoration: none;
        }
        .btn-primary:hover {
          text-decoration: none;
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
}